import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import travelDestinationObject from '@/entities/travelDestination/interface'
import travelTypeObject from '@/entities/travelType/interface'

import {
  ALL_PREFILTERED_TRAVELS_BLOCKS_QUERY,
  SINGLE_PREFILTERED_TRAVELS_BLOCK_QUERY,
  CREATE_PREFILTERED_TRAVELS_BLOCK_MUTATION,
  UPDATE_PREFILTERED_TRAVELS_BLOCK_MUTATION,
  DELETE_PREFILTERED_TRAVELS_BLOCK_MUTATION,
  FORCE_DELETE_PREFILTERED_TRAVELS_BLOCK_MUTATION,
  ADD_PREFILTERED_TRAVELS_BLOCK_DESTINATIONS_MUTATION,
  REMOVE_PREFILTERED_TRAVELS_BLOCK_DESTINATIONS_MUTATION,
  CREATE_PREFILTERED_TRAVELS_BLOCK_DESTINATIONS_MUTATION,
  DELETE_PREFILTERED_TRAVELS_BLOCK_DESTINATIONS_MUTATION,
  UPDATE_PREFILTERED_TRAVELS_BLOCK_DESTINATIONS_ORDER_MUTATION,
  ADD_PREFILTERED_TRAVELS_BLOCK_TRAVEL_TYPES_MUTATION,
  REMOVE_PREFILTERED_TRAVELS_BLOCK_TRAVEL_TYPES_MUTATION,
  CREATE_PREFILTERED_TRAVELS_BLOCK_TRAVEL_TYPES_MUTATION,
  DELETE_PREFILTERED_TRAVELS_BLOCK_TRAVEL_TYPES_MUTATION,
  UPDATE_PREFILTERED_TRAVELS_BLOCK_TRAVEL_TYPES_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const cmsPrefilteredTravelsBlockRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_PREFILTERED_TRAVELS_BLOCK_QUERY, variables).then((data: any) => {
      return hydrateObject(data.prefilteredTravelsBlock)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_PREFILTERED_TRAVELS_BLOCKS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.prefilteredTravelsBlocks)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_PREFILTERED_TRAVELS_BLOCK_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.prefilteredTravelsBlock),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_PREFILTERED_TRAVELS_BLOCKS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.prefilteredTravelsBlocks),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_PREFILTERED_TRAVELS_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.prefilteredTravelsBlock)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_PREFILTERED_TRAVELS_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.prefilteredTravelsBlock)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_PREFILTERED_TRAVELS_BLOCK_MUTATION, variables).then((data: any) => {
      return data.deleteCmsPrefilteredTravelsBlock
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_PREFILTERED_TRAVELS_BLOCK_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCmsPrefilteredTravelsBlock
    })
  },

  assignDestinations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        destinationIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_PREFILTERED_TRAVELS_BLOCK_DESTINATIONS_MUTATION, variables)
  },

  unassignDestinations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        destinationIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_PREFILTERED_TRAVELS_BLOCK_DESTINATIONS_MUTATION, variables)
  },

  createDestinations: async (id: string, items: Array<travelDestinationObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        destinations: items,
      },
    }
    return graphQLClient.request(CREATE_PREFILTERED_TRAVELS_BLOCK_DESTINATIONS_MUTATION, variables)
  },

  deleteDestinations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        destinationIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_PREFILTERED_TRAVELS_BLOCK_DESTINATIONS_MUTATION, variables)
  },

  updateDestinationsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        destinationIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_PREFILTERED_TRAVELS_BLOCK_DESTINATIONS_ORDER_MUTATION, variables)
  },

  assignTravelTypes: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelTypeIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_PREFILTERED_TRAVELS_BLOCK_TRAVEL_TYPES_MUTATION, variables)
  },

  unassignTravelTypes: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelTypeIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_PREFILTERED_TRAVELS_BLOCK_TRAVEL_TYPES_MUTATION, variables)
  },

  createTravelTypes: async (id: string, items: Array<travelTypeObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelTypes: items,
      },
    }
    return graphQLClient.request(CREATE_PREFILTERED_TRAVELS_BLOCK_TRAVEL_TYPES_MUTATION, variables)
  },

  deleteTravelTypes: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelTypeIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_PREFILTERED_TRAVELS_BLOCK_TRAVEL_TYPES_MUTATION, variables)
  },

  updateTravelTypesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelTypeIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_PREFILTERED_TRAVELS_BLOCK_TRAVEL_TYPES_ORDER_MUTATION, variables)
  },
}

export default cmsPrefilteredTravelsBlockRepository
